import { useState, type ReactNode } from "react";
import type { TabId } from "../App";
import BrandMark from "./BrandMark";

type ClusterId =
  | "overview"
  | "finance-risk"
  | "advanced-analytics"
  | "tools"
  | "resources";

interface TabDef {
  id: TabId;
  label: string;
  icon: ReactNode;
  featured?: boolean;
}

interface SidebarGroup {
  cluster: ClusterId;
  /** When undefined the group renders without a header (used for Overview). */
  label?: string;
  tabs: TabDef[];
}

// ---------- Icons (16-px line-art, currentColor) ----------
const Icon = {
  executive: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 14h12M3.5 11V7M7 11V4M10.5 11V8M13.5 11V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  portfolio: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.75" y="2.5" width="10.5" height="11" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.25 5.5h5.5M5.25 8h5.5M5.25 10.5h3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  forecast: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 12l3-3.5 2.8 2 5-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 5h3.4v3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  risk: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.75l5.25 1.9v4.6c0 3.2-2.2 5.7-5.25 6.5-3.05-.8-5.25-3.3-5.25-6.5v-4.6L8 1.75z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 5.5v3.2M8 11v.05" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  simulator: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.75 4.25h7m1.5 0h2M2.75 8h2m1.5 0h7M2.75 11.75h7m1.5 0h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="10.25" cy="4.25" r="1.4" stroke="currentColor" strokeWidth="1.4" fill="var(--bg-1)" />
      <circle cx="5.75" cy="8" r="1.4" stroke="currentColor" strokeWidth="1.4" fill="var(--bg-1)" />
      <circle cx="10.25" cy="11.75" r="1.4" stroke="currentColor" strokeWidth="1.4" fill="var(--bg-1)" />
    </svg>
  ),
  customer: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3 13.25c.5-2.3 2.6-3.6 5-3.6s4.5 1.3 5 3.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  industry: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 13.25h11M3.5 13V6.25l3 1.5V6.25l3 1.5V6.25l3 1.5V13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 9.75v.05M5.5 11.25v.05M8.5 9.75v.05M8.5 11.25v.05M11.5 9.75v.05M11.5 11.25v.05" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  oraczen: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5l1.4 3.6 3.6 1.4-3.6 1.4L8 11.5 6.6 7.9 3 6.5l3.6-1.4L8 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M3.25 11.25l.5 1.25 1.25.5-1.25.5-.5 1.25-.5-1.25L1.5 13l1.25-.5.5-1.25z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  ),
  chat: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2.5 4.25c0-.97.78-1.75 1.75-1.75h7.5c.97 0 1.75.78 1.75 1.75v5.5c0 .97-.78 1.75-1.75 1.75H7l-3 2.5v-2.5h-.25A1.25 1.25 0 0 1 2.5 9.75v-5.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.5 6.5h5M5.5 8.5h3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  reference: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3.25 2.5h7.5a1.5 1.5 0 0 1 1.5 1.5v9.5L8 11l-4.25 2.5V4a1.5 1.5 0 0 1 1.5-1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5.5 6h5M5.5 8.25h3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  faq: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6.25 6.25c0-1 .8-1.75 1.75-1.75S9.75 5.25 9.75 6.25c0 1-1.75 1.25-1.75 2.5M8 11v.05" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  about: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 7.25v3.75M8 5v.05" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  architecture: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="6" y="2" width="4" height="3" rx="0.6" stroke="currentColor" strokeWidth="1.3" />
      <rect x="2" y="11" width="4" height="3" rx="0.6" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10" y="11" width="4" height="3" rx="0.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 5v2.5M4 11V8.5h8V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// Sidebar structure:
//   Overview                              ← ungrouped
//   FINANCE & RISK                        (Decision Pack hidden)
//     Portfolio Billing
//     Net Income Forecast
//     AR & Customer Risk
//     Simulator
//   ADVANCED ANALYTICS                    ← merged Customer 360 + Industry
//     Customer 360
//     Industry
//   TOOLS                                 ← renamed from Oraczen AI
//     Zen Insights                        ← renamed from Oraczen
//     Chat
//   RESOURCES
//     Reference Architecture
//     KPI Reference
//     FAQ
//     About Oraczen
const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    cluster: "overview",
    tabs: [
      { id: "executive", label: "Overview", icon: Icon.executive },
    ],
  },
  {
    cluster: "finance-risk",
    label: "Finance & Risk",
    tabs: [
      { id: "portfolio-billing", label: "Portfolio Billing", icon: Icon.portfolio },
      { id: "net-income-forecast", label: "Net Income Forecast", icon: Icon.forecast },
      { id: "ar-customer-risk", label: "AR & Customer Risk", icon: Icon.risk },
      { id: "simulator", label: "Simulator", icon: Icon.simulator, featured: true },
    ],
  },
  {
    cluster: "advanced-analytics",
    label: "Advanced Analytics",
    tabs: [
      { id: "customer-360", label: "Customer 360", icon: Icon.customer },
      { id: "industry", label: "Industry", icon: Icon.industry },
    ],
  },
  {
    cluster: "tools",
    label: "Tools",
    tabs: [
      { id: "cortex", label: "Zen Insights", icon: Icon.oraczen, featured: true },
      { id: "chat", label: "Chat", icon: Icon.chat, featured: true },
    ],
  },
  {
    cluster: "resources",
    label: "Resources",
    tabs: [
      { id: "architecture", label: "Reference Architecture", icon: Icon.architecture },
      { id: "kpi-reference", label: "KPI Reference", icon: Icon.reference },
      { id: "faq", label: "FAQ", icon: Icon.faq },
      { id: "about", label: "About Oraczen", icon: Icon.about },
    ],
  },
];

interface Props {
  active: TabId;
  onTabChange: (id: TabId) => void;
}

export default function Sidebar({ active, onTabChange }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleSelect = (id: TabId) => {
    onTabChange(id);
    setMobileOpen(false);
  };

  return (
    <>
      <button
        className="sidebar-mobile-toggle"
        type="button"
        aria-label="Open navigation"
        onClick={() => setMobileOpen(true)}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M3 5h12M3 9h12M3 13h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`sidebar${collapsed ? " collapsed" : ""}${mobileOpen ? " mobile-open" : ""}`}
        aria-label="Primary navigation"
      >
        <div className="sidebar-head">
          <div className="sidebar-brand">
            <BrandMark size={32} className="sidebar-brand-mark" />
            {!collapsed && (
              <div className="sidebar-brand-text">
                <div className="sidebar-brand-title">Cashflow Sentinel</div>
                <div className="sidebar-brand-sub">Portfolio cash intelligence</div>
              </div>
            )}
          </div>
          <button
            type="button"
            className="sidebar-collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={() => setCollapsed((v) => !v)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d={collapsed ? "M5 3l4 4-4 4" : "M9 3l-4 4 4 4"}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav" role="tablist">
          {SIDEBAR_GROUPS.map((g) => (
            <div
              key={g.cluster}
              className={`sidebar-group${g.label ? "" : " no-label"}`}
              data-cluster={g.cluster}
            >
              {!collapsed && g.label && <div className="sidebar-group-label">{g.label}</div>}
              <ul className="sidebar-group-list">
                {g.tabs.map((t) => {
                  const isActive = active === t.id;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        className={`sidebar-tab${isActive ? " active" : ""}${t.featured ? " featured" : ""}`}
                        role="tab"
                        aria-selected={isActive}
                        title={collapsed ? t.label : undefined}
                        onClick={() => handleSelect(t.id)}
                      >
                        <span className="sidebar-tab-icon" aria-hidden="true">
                          {t.icon}
                        </span>
                        <span className="sidebar-tab-label">{t.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          {!collapsed && (
            <div className="sidebar-foot-meta">
              <div className="sidebar-foot-vintage">v2026.18</div>
              <div className="sidebar-foot-stack">Azure · D365 · MSAL SSO</div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
